/**
 * Job API Service
 *
 * Handles all API calls for workshop jobs:
 * - CRUD operations
 * - Job lifecycle (start, pause, resume, complete, cancel)
 * - Staff assignment
 * - Resource allocation (equipment, tools, spaces, consultants)
 * - Work items management
 * - Notes/comments
 * - Search and filtering
 * - Loan car incident recording
 */

import axiosInstance from './axiosInstance';

// ============================================
// CRUD Operations
// ============================================

/**
 * Get all jobs with pagination.
 * @param {{ page?: number, size?: number, myJobsOnly?: boolean, bustCache?: boolean,
 *          fromDate?: string, toDate?: string, includeOpenJobs?: boolean }} options
 *        myJobsOnly — when true, backend filters to jobs assigned to the
 *        currently logged-in user only.
 *        bustCache  — when true, appends a timestamp to the URL so iPad Safari
 *        (and any intermediate caches) cannot serve a stale response. Used by
 *        the manual refresh button.
 *        fromDate / toDate — optional ISO date strings (YYYY-MM-DD). Both must
 *        be supplied together. When provided, the backend filters jobs by
 *        scheduledStartDate within the inclusive window. Backend caps the window
 *        at one month (31 days) — larger ranges are rejected.
 *        q — customer name or rego, decided by the server; omitted when blank.
 *        includeOpenJobs — for the views that draw a BOARD rather than a month's
 *        history. The window then bounds only the finished jobs; anything still
 *        open comes back whatever month it is scheduled in. Which statuses count
 *        as open is the server's rule, not ours — do not re-derive it here.
 */
export const getJobsApi = async ({
                                     page = 0,
                                     size = 20,
                                     myJobsOnly = false,
                                     bustCache = false,
                                     fromDate = null,
                                     toDate = null,
                                     includeOpenJobs = false,
                                     q = '',
                                 } = {}) => {
    const params = { page, size };
    if (myJobsOnly) params.myJobsOnly = true;
    // The phone's search, once the typing settles. Omitted when blank.
    if (q && q.trim()) params.q = q.trim();
    if (bustCache) params._t = Date.now();
    // Both must be supplied together — backend rejects partial input.
    if (fromDate && toDate) {
        params.fromDate = fromDate;
        params.toDate   = toDate;
        // Only meaningful alongside a window: an unwindowed listing already
        // contains every open job. Absent params must be absent, not empty.
        if (includeOpenJobs) params.includeOpenJobs = true;
    }
    const response = await axiosInstance.get('/jobs', { params });
    return response.data;
};

/**
 * The queued jobs — every job still waiting to start — a page of twenty at a time.
 *
 * 🔑 Its own endpoint, its own page. Queued is the one list that grows without bound:
 * a migration lands thousands of open jobs there at once. Twenty a page, because a
 * hundred was too heavy on the wire; and the search goes to the SERVER once the typing
 * settles — it decides whether the text is a rego or a name and searches the right
 * column — while the loaded page is narrowed locally on every keystroke in between.
 *
 * @param {{ q?: string, onTv?: boolean, page?: number, size?: number, bustCache?: boolean }} options
 *        q      — omitted when blank. A bare `?q=` is a filter the server would apply.
 *                 Callers validate first (utils/searchTerm); the server refuses the rest.
 *        onTv   — true asks for only the jobs picked for the TV; omitted asks for all.
 *                 Never sent as false: "not on TV" is not a filter anyone draws.
 * @returns a Micronaut Page: `{ content, totalSize, pageable, … }`
 */
export const getQueuedJobsApi = async ({
                                           q = '',
                                           onTv = null,
                                           page = 0,
                                           size = QUEUED_PAGE_SIZE,
                                           bustCache = false,
                                       } = {}) => {
    const params = { page, size };
    if (q && q.trim()) params.q = q.trim();
    if (onTv === true) params.onTv = true;
    if (bustCache) params._t = Date.now();
    const response = await axiosInstance.get('/jobs/queued', { params });
    return response.data;
};

/** A page of queued jobs: twenty, which is the server's cap. */
export const QUEUED_PAGE_SIZE = 20;

/** A page of the phone's all-jobs list: the same twenty, from the month query. */
export const MOBILE_JOBS_PAGE_SIZE = 20;

/**
 * Pick a job for the TV, or take it off.
 *
 * TV Mode shows only the jobs a person has chosen for the wall — the ones the floor
 * needs to see — rather than everything on the board. This is that choice.
 */
export const setJobOnTvApi = async (uuid, onTv) => {
    const response = await axiosInstance.patch(`/jobs/${uuid}/tv`, { onTv: !!onTv });
    return response.data;
};

/**
 * Get a single job by UUID.
 */
export const getJobApi = async (uuid) => {
    const response = await axiosInstance.get(`/jobs/${uuid}`);
    return response.data;
};

/**
 * Create a job from an existing booking.
 */
export const createJobFromBookingApi = async (data) => {
    const response = await axiosInstance.post('/jobs/from-booking', data);
    return response.data;
};

/**
 * Create a standalone job (no booking).
 */
export const createStandaloneJobApi = async (data) => {
    const response = await axiosInstance.post('/jobs', data);
    return response.data;
};

/**
 * Update an existing job.
 */
export const updateJobApi = async (uuid, data) => {
    const response = await axiosInstance.put(`/jobs/${uuid}`, data);
    return response.data;
};

// ============================================
// Job Lifecycle
// ============================================

/**
 * Update job status.
 */
export const updateJobStatusApi = async (uuid, status) => {
    const response = await axiosInstance.patch(`/jobs/${uuid}/status`, { status });
    return response.data;
};

/**
 * Start work on a job.
 */
export const startJobApi = async (uuid) => {
    const response = await axiosInstance.patch(`/jobs/${uuid}/start`);
    return response.data;
};

/**
 * Pause a job.
 */
export const pauseJobApi = async (uuid, reason = null) => {
    const response = await axiosInstance.patch(`/jobs/${uuid}/pause`, { reason });
    return response.data;
};

/**
 * Resume a paused job.
 */
export const resumeJobApi = async (uuid) => {
    const response = await axiosInstance.patch(`/jobs/${uuid}/resume`);
    return response.data;
};

/**
 * Complete a job.
 */
export const completeJobApi = async (uuid, odometerOut = null) => {
    const response = await axiosInstance.patch(`/jobs/${uuid}/complete`, { odometerOut });
    return response.data;
};

/**
 * Record the vehicle-in odometer reading.
 *
 * Scoped single-field write so the technician can capture or correct the
 * reading at any point in the job lifecycle without sending the full
 * UpdateJobRequest payload. Returns the full JobDetailResponse so the
 * caller can rehydrate the modal state.
 */
export const recordOdometerInApi = async (uuid, odometerIn) => {
    const response = await axiosInstance.patch(`/jobs/${uuid}/odometer-in`, { odometerIn });
    return response.data;
};

/**
 * Cancel a job.
 */
export const cancelJobApi = async (uuid, reason = null) => {
    const response = await axiosInstance.delete(`/jobs/${uuid}`, {
        params: { reason }
    });
    return response.data;
};

/**
 * Hard-delete a job along with its linked booking and invoice.
 *
 * Backed by DELETE /api/jobs/{uuid}/purge — irreversible. The server
 * cascades through invoice line items, confirmation requests, job notes,
 * work items, loan car allocations, then the booking and invoice rows.
 * Returns 204 No Content on success.
 */
export const purgeJobApi = async (uuid) => {
    await axiosInstance.delete(`/jobs/${uuid}/purge`);
    return { uuid };
};

// ============================================
// Staff Assignment
// ============================================

/**
 * Assign staff to a job.
 */
export const assignStaffApi = async (uuid, staffUuid) => {
    const response = await axiosInstance.patch(`/jobs/${uuid}/assign`, { staffUuid });
    return response.data;
};

/**
 * Unassign staff from a job.
 */
export const unassignStaffApi = async (uuid) => {
    const response = await axiosInstance.patch(`/jobs/${uuid}/unassign`);
    return response.data;
};

// ============================================
// Resource Allocation
// ============================================

/**
 * Get all resources currently assigned to a job.
 */
export const getJobResourcesApi = async (jobUuid) => {
    const response = await axiosInstance.get(`/jobs/${jobUuid}/resources`);
    return response.data;
};

/**
 * Get available (unallocated, active) resources for a job.
 * Optionally filter by type: EQUIPMENT | TOOL | SPACE | CONSULTANT
 * Loan cars are excluded — they are managed through their own booking flow.
 */
export const getAvailableResourcesApi = async (jobUuid, type = null) => {
    const params = type ? { type } : {};
    const response = await axiosInstance.get(`/jobs/${jobUuid}/resources/available`, { params });
    return response.data;
};

/**
 * Assign a resource to a job.
 * @param {string} jobUuid
 * @param {{ resourceUuid: string, fromDate?: string, toDate?: string, allocationNotes?: string }} data
 */
export const assignResourceApi = async (jobUuid, data) => {
    const response = await axiosInstance.post(`/jobs/${jobUuid}/resources`, data);
    return response.data;
};

/**
 * Release a resource from a job.
 */
export const unassignResourceApi = async (jobUuid, resourceUuid) => {
    const response = await axiosInstance.delete(`/jobs/${jobUuid}/resources/${resourceUuid}`);
    return response.data;
};

// ============================================
// Work Item Operations
// ============================================

/**
 * Add a work item to a job.
 */
export const addWorkItemApi = async (jobUuid, data) => {
    const response = await axiosInstance.post(`/jobs/${jobUuid}/work-items`, data);
    return response.data;
};

/**
 * Update a work item.
 */
export const updateWorkItemApi = async (jobUuid, workItemUuid, data) => {
    const response = await axiosInstance.put(`/jobs/${jobUuid}/work-items/${workItemUuid}`, data);
    return response.data;
};

/**
 * Update work item status.
 */
export const updateWorkItemStatusApi = async (jobUuid, workItemUuid, status) => {
    const response = await axiosInstance.patch(`/jobs/${jobUuid}/work-items/${workItemUuid}/status`, { status });
    return response.data;
};

/**
 * Remove a work item from a job.
 */
export const removeWorkItemApi = async (jobUuid, workItemUuid) => {
    const response = await axiosInstance.delete(`/jobs/${jobUuid}/work-items/${workItemUuid}`);
    return response.data;
};

/**
 * Bulk-start every PENDING work item on a job in one server-side transaction.
 * Items in other statuses are silently left as-is. Returns the full updated job
 * detail response.
 */
export const startAllWorkItemsApi = async (jobUuid) => {
    const response = await axiosInstance.patch(`/jobs/${jobUuid}/work-items/start-all`);
    return response.data;
};

/**
 * Bulk-complete every non-terminal work item on a job in one server-side
 * transaction. For profile-seeded items the OUT measurement list is auto-filled
 * with the green/OK value so the OUT-readings-complete gate doesn't block the
 * transition. Items already COMPLETED/DECLINED are skipped; AWAITING_CONFIRMATION
 * items are skipped server-side. Returns the full updated job detail response.
 */
export const completeAllWorkItemsApi = async (jobUuid) => {
    const response = await axiosInstance.patch(`/jobs/${jobUuid}/work-items/complete-all`);
    return response.data;
};

// ============================================
// Measurement Observation Operations
// ============================================

/**
 * Record (upsert) a measurement observation value on a work item.
 * The backend evaluates the value against the measurement's green/amber/red
 * threshold config and returns the updated full job detail response.
 *
 * For multi-position measurements (e.g. tyres with LF/RF/LR/RR), pass the
 * positionLabel to identify which specific position is being recorded.
 * For measurements with sub-positions (e.g. tyre tread OUTER/MIDDLE/INNER),
 * also pass subPositionLabel to identify which cell within that position.
 * For single-slot measurements both labels are null/omitted.
 *
 * The triple (measurementUuid, positionLabel, subPositionLabel) uniquely
 * identifies one observation row on a work item — the backend rejects requests
 * where the labels don't match the measurement's declared dimensions.
 *
 * The {@code phase} parameter routes the observation into either the IN
 * (pre-work / baseline) or OUT (post-work) list on the work item. Defaults
 * to "IN" when omitted — matches the historical behaviour from before the
 * IN/OUT split, so any caller that hasn't been updated still records baseline.
 *
 * @param {string} jobUuid
 * @param {string} workItemUuid
 * @param {string} measurementUuid
 * @param {string} value  — the technician's observed value string
 * @param {string|null} positionLabel    — position label for multi-position measurements (e.g. "LF")
 * @param {string|null} subPositionLabel — sub-position label when applicable (e.g. "OUTER")
 * @param {'IN'|'OUT'} phase — which observation list to write to (default 'IN')
 */
/**
 * Record SEVERAL observations on one work item in ONE request.
 *
 * ⚠️ Not an optimisation — a correctness fix. Observations live in a JSON column, so
 * each single-value call rewrites the whole list; firing N of them concurrently means
 * they all read the same original and the last one wins. Filling a tyre position's
 * OUTER/MIDDLE/INNER from the status slider did exactly that and silently kept one
 * reading of three, with every request returning 200.
 */
export const recordObservationsApi = async (jobUuid, workItemUuid, observations, phase = 'IN') => {
    const response = await axiosInstance.post(
        `/jobs/${jobUuid}/work-items/${workItemUuid}/observations/bulk?phase=${phase}`,
        {
            observations: observations.map(({ measurementUuid, value, positionLabel, subPositionLabel }) => {
                const entry = { measurementUuid, value };
                // Absent, not null: the server validates position/sub-position against
                // what the measurement declares, and an explicit null on a measurement
                // with no sub-positions reads differently from omitting it.
                if (positionLabel != null) entry.positionLabel = positionLabel;
                if (subPositionLabel != null) entry.subPositionLabel = subPositionLabel;
                return entry;
            }),
        },
    );
    return response.data;
};

export const recordObservationApi = async (
    jobUuid, workItemUuid, measurementUuid, value,
    positionLabel = null, subPositionLabel = null, phase = 'IN'
) => {
    const body = { measurementUuid, value };
    if (positionLabel != null) {
        body.positionLabel = positionLabel;
    }
    if (subPositionLabel != null) {
        body.subPositionLabel = subPositionLabel;
    }
    const response = await axiosInstance.post(
        `/jobs/${jobUuid}/work-items/${workItemUuid}/observations?phase=${phase}`,
        body
    );
    return response.data;
};

/**
 * Link a measurement to an ad-hoc (free-form) work item.
 * Creates a blank placeholder observation (value="", status=UNCLASSIFIED) so the
 * UI can render the input row before the technician has entered a value.
 *
 * For multi-position measurements, the backend automatically expands the link
 * into one placeholder per position label.
 *
 * Backend rejects this for profile-seeded work items (their measurement set is fixed).
 *
 * @param {string} jobUuid
 * @param {string} workItemUuid
 * @param {string} measurementUuid
 * @returns {Promise<Array>} updated list of ObservationResponse for the work item
 */
export const linkMeasurementApi = async (jobUuid, workItemUuid, measurementUuid) => {
    const response = await axiosInstance.post(
        `/jobs/${jobUuid}/work-items/${workItemUuid}/observations/link`,
        { measurementUuid }
    );
    return response.data;
};

/**
 * Unlink a measurement observation from an ad-hoc (free-form) work item.
 * Backend rejects this for profile-seeded work items.
 *
 * @param {string} jobUuid
 * @param {string} workItemUuid
 * @param {string} measurementUuid
 * @returns {Promise<Array>} updated list of ObservationResponse for the work item
 */
export const unlinkMeasurementApi = async (jobUuid, workItemUuid, measurementUuid) => {
    const response = await axiosInstance.delete(
        `/jobs/${jobUuid}/work-items/${workItemUuid}/observations/${measurementUuid}`
    );
    return response.data;
};

/**
 * List all measurements visible to the current organisation
 * (org-owned + global). Used by the "+ Add measurement" picker on
 * ad-hoc work items.
 *
 * @param {{ activeOnly?: boolean, page?: number, size?: number }} options
 * @returns {Promise<{content: Array, totalElements: number, ...}>}
 */
export const listMeasurementsApi = async ({ activeOnly = true, page = 0, size = 200 } = {}) => {
    const response = await axiosInstance.get('/profile-work-items/measurements', {
        params: { activeOnly, page, size },
    });
    return response.data;
};

// ============================================
// Work Item Category Operations (Ad-hoc)
// ============================================

/**
 * Get all ad-hoc work item categories on a job.
 */
export const getWorkItemCategoriesApi = async (jobUuid) => {
    const response = await axiosInstance.get(`/jobs/${jobUuid}/work-item-categories`);
    return response.data;
};

/**
 * Add an ad-hoc WorkItemCategory to a job.
 * Creates the category, a JobWorkItem, and an InvoiceLineItem on the DRAFT invoice.
 */
export const addWorkItemCategoryApi = async (jobUuid, data) => {
    const response = await axiosInstance.post(`/jobs/${jobUuid}/work-item-categories`, data);
    return response.data;
};

/**
 * Update an existing ad-hoc WorkItemCategory work item on a job.
 */
export const updateWorkItemCategoryApi = async (jobUuid, workItemUuid, data) => {
    const response = await axiosInstance.put(`/jobs/${jobUuid}/work-item-categories/${workItemUuid}`, data);
    return response.data;
};

/**
 * Remove an ad-hoc WorkItemCategory work item from a job.
 */
export const removeWorkItemCategoryApi = async (jobUuid, workItemUuid) => {
    const response = await axiosInstance.delete(`/jobs/${jobUuid}/work-item-categories/${workItemUuid}`);
    return response.data;
};

// ============================================
// Note Operations
// ============================================

/**
 * Add a note to a job.
 */
export const addJobNoteApi = async (jobUuid, data) => {
    const response = await axiosInstance.post(`/jobs/${jobUuid}/notes`, data);
    return response.data;
};

// ============================================
// Filter and Search
// ============================================

/**
 * Get jobs by status.
 */
export const getJobsByStatusApi = async (status) => {
    const response = await axiosInstance.get(`/jobs/status/${status}`);
    return response.data;
};

/**
 * Get my active jobs (for current logged-in staff).
 */
export const getMyJobsApi = async () => {
    const response = await axiosInstance.get('/jobs/my-jobs');
    return response.data;
};

/**
 * Get jobs for a specific staff member.
 */
export const getJobsByStaffApi = async (staffUuid) => {
    const response = await axiosInstance.get(`/jobs/staff/${staffUuid}`);
    return response.data;
};

/**
 * Get unassigned jobs.
 */
export const getUnassignedJobsApi = async () => {
    const response = await axiosInstance.get('/jobs/unassigned');
    return response.data;
};

/**
 * Get jobs in progress.
 */
export const getInProgressJobsApi = async () => {
    const response = await axiosInstance.get('/jobs/in-progress');
    return response.data;
};

/**
 * Get paused jobs.
 */
export const getPausedJobsApi = async () => {
    const response = await axiosInstance.get('/jobs/paused');
    return response.data;
};

/**
 * Get jobs for a customer.
 */
export const getJobsByCustomerApi = async (customerUuid) => {
    const response = await axiosInstance.get(`/jobs/customer/${customerUuid}`);
    return response.data;
};

/**
 * Get jobs for a vehicle.
 */
export const getJobsByVehicleApi = async (vehicleUuid) => {
    const response = await axiosInstance.get(`/jobs/vehicle/${vehicleUuid}`);
    return response.data;
};

/**
 * Search jobs by query (job number).
 */
export const searchJobsApi = async (query, { page = 0, size = 20 } = {}) => {
    const response = await axiosInstance.get('/jobs/search', {
        params: { q: query, page, size }
    });
    return response.data;
};

/**
 * Get job summary counts.
 */
export const getJobSummaryApi = async () => {
    const response = await axiosInstance.get('/jobs/summary');
    return response.data;
};

// ============================================
// Enum / Metadata
// ============================================

/**
 * Get all WorkItemCategory CategoryType enum values.
 * Returns [{ value, label, description }]
 */
export const getCategoryTypesApi = async () => {
    const response = await axiosInstance.get('/jobs/meta/category-types');
    return response.data;
};

/**
 * Get all JobWorkItem WorkCategory enum values.
 * Deprecated values will have label "Not Supported".
 * Returns [{ value, label, description }]
 */
export const getWorkCategoriesApi = async () => {
    const response = await axiosInstance.get('/jobs/meta/work-categories');
    return response.data;
};

/**
 * Request job-level customer approval (PROFILE approval mode).
 * Sends a single authorisation request covering all work items on the job.
 *
 * @param {string} jobUuid
 * @param {string} description — message to send to the customer
 */
export const requestJobApprovalApi = async (jobUuid, description) => {
    const response = await axiosInstance.post(`/jobs/${jobUuid}/request-approval`, {
        description: description.trim(),
    });
    return response.data;
};

/**
 * Mark a job as vehicle collected (customer has picked up).
 * Removes the job from the active board and marks the booking as completed.
 * The response includes loanCarRego if the booking had a loan car attached —
 * the UI uses this to conditionally show the loan car return modal.
 */
/**
 * @param nextService `{ nextServiceDue, nextServiceKm }`, either of which may be null
 *
 * ⚠️ The body is optional on the server and stays optional here. Sending nothing
 * collects the vehicle exactly as before and leaves the vehicle's next-service alone —
 * saying nothing must not clear a date somebody set.
 */
export const vehicleCollectedApi = async (uuid, nextService = null) => {
    const body = nextService && (nextService.nextServiceDue || nextService.nextServiceKm)
        ? {
            nextServiceDue: nextService.nextServiceDue || null,
            nextServiceKm: nextService.nextServiceKm ?? null,
        }
        : undefined;
    const response = await axiosInstance.patch(`/jobs/${uuid}/vehicle-collected`, body);
    return response.data;
};

/**
 * Update the planner order (break-even sequence number) for a job.
 * Sends only plannerOrder via the standard PUT /jobs/:uuid endpoint.
 */
export const updateJobPlannerOrderApi = async (uuid, plannerOrder) => {
    const response = await axiosInstance.put(`/jobs/${uuid}`, { plannerOrder });
    return response.data;
};

// ============================================
// Loan Car Incidents
// ============================================

/**
 * Upload a single loan car handover photo to S3.
 * Returns { s3Key, category }.
 *
 * Call this once per photo before submitting the return payload.
 * Pass the returned s3Key values inside imagesByCategory.
 *
 * @param {string} category  — ImageCategory enum value, e.g. 'CAR_DASHBOARD_ON_VEHICLE_START'
 * @param {File}   file      — the photo File from an <input type="file">
 */
export const uploadLoanCarPhotoApi = async (category, file) => {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await axiosInstance.post(
        `/loan-car-incidents/upload-photo?category=${encodeURIComponent(category)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
};

/**
 * Record a loan car IN (return) incident for the booking linked to a job.
 *
 * Photo S3 keys must be obtained via uploadLoanCarPhotoApi first, then passed
 * in the imagesByCategory array. Validation on the backend requires:
 *   - odometerReading (mandatory, non-negative)
 *   - at least one CAR_DASHBOARD_ON_VEHICLE_START image
 *
 * @param {string} jobUuid
 * @param {{
 *   odometerReading: number,
 *   notes?: string,
 *   imagesByCategory: Array<{ category: string, images: Array<{ s3Key: string }> }>
 * }} payload
 */
export const recordLoanCarReturnApi = async (jobUuid, payload) => {
    const response = await axiosInstance.post(
        `/loan-car-incidents/job/${jobUuid}/return`,
        payload
    );
    return response.data;
};

/**
 * Fetch all loan car incidents (OUT + IN) recorded against a job's booking.
 * Images are resolved to 15-minute pre-signed URLs by the backend.
 *
 * @param {string} jobUuid
 */
export const getLoanCarIncidentsForJobApi = async (jobUuid) => {
    const response = await axiosInstance.get(`/loan-car-incidents/job/${jobUuid}`);
    return response.data;
};

// ============================================
// Default Export
// ============================================

const jobApi = {
    // CRUD
    getJobsApi,
    getJobApi,
    createJobFromBookingApi,
    createStandaloneJobApi,
    updateJobApi,
    // Lifecycle
    updateJobStatusApi,
    startJobApi,
    pauseJobApi,
    resumeJobApi,
    completeJobApi,
    recordOdometerInApi,
    cancelJobApi,
    // Staff Assignment
    assignStaffApi,
    unassignStaffApi,
    // Vehicle Collected
    vehicleCollectedApi,
    // Job-level Approval
    requestJobApprovalApi,
    // Planner Order
    updateJobPlannerOrderApi,
    // Resource Allocation
    getJobResourcesApi,
    getAvailableResourcesApi,
    assignResourceApi,
    unassignResourceApi,
    // Work Items
    addWorkItemApi,
    updateWorkItemApi,
    updateWorkItemStatusApi,
    removeWorkItemApi,
    startAllWorkItemsApi,
    completeAllWorkItemsApi,
    // Measurement Observations
    recordObservationApi,
    recordObservationsApi,
    linkMeasurementApi,
    unlinkMeasurementApi,
    listMeasurementsApi,
    // Work Item Categories (Ad-hoc)
    getWorkItemCategoriesApi,
    addWorkItemCategoryApi,
    updateWorkItemCategoryApi,
    removeWorkItemCategoryApi,
    // Notes
    addJobNoteApi,
    // Filter & Search
    getJobsByStatusApi,
    getMyJobsApi,
    getJobsByStaffApi,
    getUnassignedJobsApi,
    getInProgressJobsApi,
    getPausedJobsApi,
    getJobsByCustomerApi,
    getJobsByVehicleApi,
    searchJobsApi,
    getJobSummaryApi,
    // Enums / Metadata
    getCategoryTypesApi,
    getWorkCategoriesApi,
    // Loan Car Incidents
    uploadLoanCarPhotoApi,
    recordLoanCarReturnApi,
    getLoanCarIncidentsForJobApi,
};

export default jobApi;

/**
 * Books in a job that has no booking — the quote path, where an approved quote becomes a
 * queued job with no appointment.
 *
 * ⚠️ The customer and vehicle are NOT sent: the server takes them from the job, because
 * they were settled when the customer approved the quote against that vehicle.
 */
export const scheduleJobApi = async (jobUuid, { scheduledDate, scheduledTime }) => {
    const response = await axiosInstance.post(`/jobs/${jobUuid}/booking`, {
        scheduledDate,
        scheduledTime: scheduledTime || null,
    });
    return response.data;
};

/**
 * Marks the job's booking as VEHICLE_RECEIVED, so a mechanic holding the keys does not
 * have to find the booking to say the car has arrived.
 *
 * ⚠️ `loanCarDetails` is REQUIRED when the booking has a loan car — the backend rejects
 * an empty handover on those, and rightly so: the licence and dashboard photos are the
 * evidence of what condition the loan car left in. The caller collects them with the
 * same VehicleReceivedModal the bookings screen uses, and posts the SAME body shape.
 */
export const markJobVehicleReceivedApi = async (jobUuid, loanCarDetails = null) => {
    const response = await axiosInstance.post(`/jobs/${jobUuid}/vehicle-received`, {
        loanCarDetails,
    });
    return response.data;
};
